import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, useCallback } from 'react';

import useQueryParams from 'hooks/useQuery';
import * as ErrorLogging from 'services/ErrorLogging';
import RequestService from 'services/RequestService';

const controlGroup = { isVariant: false };

/**
 * A hook for fetching a user's existing assignment for a given experiment.
 * This hook will NOT assign a user to an experiment. Use `hooks/useAssignExperiment.js` for that.
 *
 * See doc/experiments/README.md for more details.
 *
 * @typedef {Object} ExperimentData
 * @property {boolean} isVariant Whether the experiment group is a variant group
 * @property {string} experimentName The name of the experiment
 * @property {string} experimentGroupId the hash ID of the experiment group
 * @property {string} experimentGroupSlug the slug of the experiment group
 *
 * @param {string} name The name of the experiment (see: lib/constants/experimentConstants.js)
 * @param {Array} [dependencies=[]] An array of dependencies on which to re-fetch
 * @returns {ExperimentData}
 *
 */
const useFetchExperimentAssignment = (name, dependencies = []) => {
  const [experimentAssignment, setExperimentAssignment] = useState();
  const { queryParams } = useQueryParams();
  const experimentParam = queryParams.get(name);

  const getExperiment = useCallback(async () => {
    const response = await RequestService.v3.getExperiment({ params: { name } });
    if (response.ok && response.experiment) {
      const group = response.experiment.groups.find(group => group.id === experimentParam);
      if (group) {
        setExperimentAssignment({
          isVariant: group.variant,
          experimentGroupId: group.id,
          experimentGroupSlug: group.slug,
        });
      }
    }
  }, [experimentParam, name]);

  useEffect(() => {
    if (experimentParam) getExperiment();
  }, [experimentParam, getExperiment]);

  const {
    isError,
    data: response,
    error,
  } = useQuery({
    queryKey: ['experiment', name, ...dependencies],
    queryFn: ({ queryKey }) => {
      const [, name] = queryKey;
      return RequestService.v3.getExperimentAssignment({ params: { name } });
    },
    staleTime: 1000 * 60 * 2, // Read from cache for 2 minutes
    enabled: !experimentParam,
  });

  if (experimentParam) {
    return experimentAssignment || controlGroup;
  } else if (isError) {
    ErrorLogging.captureException(error);
    return controlGroup;
  } else {
    return response?.experimentAssignment || controlGroup;
  }
};

export default useFetchExperimentAssignment;
